import ChartBarIcon from '@mdi/svg/svg/chart-bar.svg?react';
import ChartLineIcon from '@mdi/svg/svg/chart-line.svg?react';
import Sort from '@mdi/svg/svg/sort-variant.svg?react';
import { type SiteId } from '@solinkcorp/api-types';
import { type LocaleDefinition } from '@solinkcorp/locale';
import LineGraph from '@solinkcorp/solink-components/svg/line-graph.svg?react';
import {
  ALL_LOCATIONS,
  type AnyWidget,
  HISTOGRAM_RANGE,
  type HistogramRange,
  RANGE_DETAIL_DEFAULT_VALUE_BY_RANGE,
  type ReportWidgetSettings,
  THRESHOLD_RANGE_MAP,
  type ThresholdRange,
  WIDGET_DATA_CATEGORY_OTHER,
  WIDGET_DATA_STATISTIC_COUNT,
  WIDGET_DEFINITIONS,
  WIDGET_INHERIT_VALUE,
  WIDGET_TYPE_REPORT,
} from '@solinkcorp/vd-common';
import { type EventClientQueryParameters } from 'clients/EventClient';
import { enableDigestIfDisabled } from 'components/SettingsPage/utils';
import merge from 'lodash/merge';
import AnalyticsLogger from 'models/AnalyticsLogger';
import AuthService from 'models/AuthService';
import { type FilterState } from 'reducers/filterReducer';
import { type DateQuery } from 'src/hooks/useSolinkEvents';
import { DEFAULT_EVENT_FITLERS } from 'stores/EventFilterStore';
import { LocationStore } from 'stores/LocationStore';
import HierarchyUtil from 'utils/HierarchyUtil';
import { OWNER_TYPE } from 'vd-constants';
import { DASHBOARD_TAB } from './constants';
import HomeHelper from './HomeHelper';
import { type FetchEventsParams } from './types';

export const FOOTER_HEIGHT = 60;

export const WIDGET_MODE = {
  INVESTIGATE: 'investigate',
  PREVIEW: 'preview',
  CARD: 'card',
};

export function eventFetchParamsToEventClientQueryParameters(
  fetchEventsParams: FetchEventsParams,
): { query: EventClientQueryParameters; dateQuery: { start: number; end: number } } {
  const query: EventClientQueryParameters = {
    eventFilterState: fetchEventsParams.eventFilterState ?? DEFAULT_EVENT_FITLERS,
    locationId: fetchEventsParams.locationId,
    locationIds: fetchEventsParams.locationIds,
    selectedReportId: fetchEventsParams.reportTemplateId,
    eqShouldFields: fetchEventsParams.eqShouldFields,
    eqMustHaveFields: fetchEventsParams.eqMustHaveFields,
    eqMustNonZeroFields: fetchEventsParams.eqMustNonZeroFields,
    includeTypes: fetchEventsParams.includeTypes,
    include: fetchEventsParams.include,
    searchString: fetchEventsParams.searchString,
    searchFilters: fetchEventsParams.searchFilters,
  };

  const dateQuery: DateQuery = {
    start: fetchEventsParams.startTime,
    end: fetchEventsParams.endTime,
  };

  return { query, dateQuery };
}

/**
 * Only user with a higher or equal level of permission with the widget can edit it directly.
 * For example, an admin user (customer-level) can edit customer-level widgets, but cannot edit
 * reseller-level widgets.
 * A cloud user can impersonate any user and edit any widget except for cloud-level ones.
 * No one should edit a cloud-level widget directly in VD.
 */
export const canUserEditWidgetDirectly = (widget: AnyWidget) => {
  const { ownerType } = widget;
  return (
    ownerType !== OWNER_TYPE.CLOUD &&
    (HierarchyUtil.isTypeEqualOrHigher({
      type: AuthService.getSession()?.userType,
      minType: ownerType,
    }) ||
      HierarchyUtil.isTypeEqualOrHigher({
        type: AuthService.impersonator?.profile.userType,
        minType: ownerType,
      }))
  );
};

/**
 * Check if a widget is included in daily or weekly digest
 */
export const isWidgetInDigest = (widgetSettings: {
  excludeFromDigest?: ReportWidgetSettings['excludeFromDigest'];
}) => {
  const { excludeFromDigest } = widgetSettings;
  return !excludeFromDigest?.daily || !excludeFromDigest?.weekly;
};

/**
 * When user add or change a widget to be included in digest,
 * enable digest for the user if it was disabled
 */
export const enableDigestOnWidgetChange = (widgetSettings: {
  excludeFromDigest?: ReportWidgetSettings['excludeFromDigest'];
}) => {
  if (isWidgetInDigest(widgetSettings)) {
    enableDigestIfDisabled();
  }
};

/**
 * Track user's action of drilling down from a widget for analytics purpose
 */
export const trackWidgetDrilldown = ({
  destinationPage,
  target,
  widget,
  globalFilters,
}: {
  destinationPage: string;
  target: { [key: string]: any };
  widget: AnyWidget;
  globalFilters: FilterState;
}) => {
  const {
    id,
    widgetSettings: { type },
  } = widget;
  AnalyticsLogger.homeWidgetDrillDown({
    id,
    type,
    destinationPage,
    target,
    filters: HomeHelper.getWidgetFilters({ filters: globalFilters, widget }),
  });
};

/**
 * Track widget's loading error for analytics purpose
 */
export const trackWidgetError = ({
  widget,
  globalFilters,
}: {
  widget: AnyWidget;
  globalFilters: FilterState;
}) => {
  const {
    id,
    description,
    siteIds,
    name,
    reportTemplateIds,
    widgetSettings: settings,
    widgetSettings: { type },
  } = widget;
  AnalyticsLogger.homeWidgetFailedToLoad({
    id,
    type,
    settings: {
      description,
      siteIds: siteIds as SiteId[],
      name,
      reportTemplateIds,
      ...settings,
    },
    filters: HomeHelper.getWidgetFilters({ filters: globalFilters, widget }),
  });
};

/**
 * Track widget's successful loading time for analytics purpose
 */
export const trackWidgetLoad = ({
  loadStartTime,
  widget,
  globalFilters,
}: {
  loadStartTime: number; // in milliseconds
  widget: AnyWidget;
  globalFilters: FilterState;
}) => {
  const {
    id,
    description,
    siteIds,
    name,
    reportTemplateIds,
    widgetSettings: settings,
    widgetSettings: { type },
  } = widget;

  const loadTimeInSeconds = (Date.now() - loadStartTime) / 1000;

  AnalyticsLogger.homeWidgetLoadTime({
    loadTimeInSeconds,
    id,
    type,
    settings: {
      description,
      siteIds: siteIds as SiteId[],
      name,
      reportTemplateIds,
      ...settings,
    },
    filters: HomeHelper.getWidgetFilters({ filters: globalFilters, widget }),
  });
};

export const widgetAnalyticsActions = {
  drilldown: trackWidgetDrilldown,
  error: trackWidgetError,
  load: trackWidgetLoad,
};

export const getThresholdChartRange = (range: ThresholdRange, isReport: boolean) => {
  return isReport ? THRESHOLD_RANGE_MAP[range] : range;
};

/**
 * Get the range rolling value for threshold chart.
 * For threshold we always display the last N range, so it's always rolling for the chart.
 * but for week range, we actually support rolling and non-rolling range, so we need the difference.
 */
export const getThresholdRangeRolling = (rangeRolling: boolean, range: HistogramRange) => {
  return range === HISTOGRAM_RANGE.WEEK ? rangeRolling : true;
};

/**
 * Get the percent threshold chart view options to display as the chart view toggle
 */
export const getPercentChartOptions = (locale: LocaleDefinition) => [
  {
    label: locale.dashboard.barChart,
    value: false,
    icon: ChartBarIcon,
    iconSize: 14,
  },
  {
    label: locale.dashboard.lineChart,
    value: true,
    icon: ChartLineIcon,
    iconSize: 14,
  },
];

/**
 * Get the chart view options to display as the chart view toggle.
 * Only for report widget and ranking widget.
 */
export const getChartViewOptions = (locale: LocaleDefinition) => [
  {
    label: locale.dashboard.summary,
    value: true,
    icon: Sort,
    iconSize: 18,
  },
  {
    label: locale.dashboard.detail,
    value: false,
    icon: LineGraph,
    iconSize: 18,
  },
];

const EMPTY_ADD_WIDGET_DATA = merge({}, WIDGET_DEFINITIONS.report.initValues, {
  reportTemplateIds: [],
  siteIds: [WIDGET_INHERIT_VALUE],
  name: '',
  description: '',
  ownerType: 'user', // default user level widgets
  widgetSettings: {
    type: WIDGET_TYPE_REPORT,
    size: 3,
    dataCategory: WIDGET_DATA_CATEGORY_OTHER,
    dataStatistic: WIDGET_DATA_STATISTIC_COUNT,
  },
});

/**
 * Get the initial widget data for the settings form of adding a new widget
 */
export const getInitialWidgetData = (
  dashboardTab: string,
  dashboardFilterRange: HistogramRange,
) => {
  return {
    ...EMPTY_ADD_WIDGET_DATA,
    widgetSettings: {
      ...EMPTY_ADD_WIDGET_DATA.widgetSettings,
      rangeDetails: RANGE_DETAIL_DEFAULT_VALUE_BY_RANGE[dashboardFilterRange],
      rangeRolling: dashboardFilterRange === HISTOGRAM_RANGE.HOUR,
      summaryView: HomeHelper.isWidgetSummaryView(EMPTY_ADD_WIDGET_DATA),
      highlightWords: [
        {
          colour: 'default',
          terms: AuthService.getSession()?.clientSettings.eventTicker?.highlightWords || [],
        },
      ],
      /**
       * Automatically add widget tag for the currently selected dashboard tab.
       * If they are on the 'All' tab, apply the 'Main' tag
       */
      tags: dashboardTab === DASHBOARD_TAB.ALL ? [DASHBOARD_TAB.MAIN] : [dashboardTab],
    },
  };
};

/** Get the initial widget with complete widget data fields */
export const getInitialWidget = (dashboardTab: string, dashboardFilterRange: HistogramRange) => {
  return {
    id: '',
    parentWidgetIds: ['any'],
    inheritType: 'user',
    cloudId: 'any',
    resellerId: 'any',
    customerId: 'any',
    thresholds: [],
    ...getInitialWidgetData(dashboardTab, dashboardFilterRange),
  };
};

export const determineDeviceIds = (siteIds: string[]): string[] => {
  if (siteIds.length === 0) {
    return [];
  }
  if (siteIds.includes(ALL_LOCATIONS)) {
    return [ALL_LOCATIONS];
  }
  const sitesById = LocationStore.getLocationsById() || {};
  const deviceIds = [];
  for (const siteId of siteIds) {
    const site = sitesById[siteId as SiteId];
    if (!site || !site.devices || !site.devices.length) {
      continue;
    }
    const siteDeviceIds = site.devices.map((device) => device.id);
    deviceIds.push(...siteDeviceIds);
  }
  return deviceIds;
};

export function shouldHideInDashboard(widgetType: AnyWidget['widgetSettings']['type']) {
  return widgetType === 'ticker';
}
